// Self-hosted build: HeyVRAd is replaced by a no-op stub (same TypeName).
// See MDs/Colyseus-Migration-Plan.md.
import { HeyVRAd } from './net/heyvr-ad-stub.js';
// Side-effect import: ensure the Arena net singleton (Colyseus + REST) is
// bundled into the WLE component output. Must be reachable from a registered
// component so esbuild traces it during editor packaging.
import './net/arena.js';
import {CollisionComponent, Component, Property} from '@wonderlandengine/api';
import { Cursor, CursorTarget, HowlerAudioSource } from '@wonderlandengine/components';
import { vec3 } from 'gl-matrix';

/**
 * lobby-handler
 */
export class LobbyHandler extends Component {
    static TypeName = 'lobby-handler';
    /* Properties that are configurable in the editor */
    static Properties = {
        btnObj: Property.object(),
        playerObj: Property.object(),
        arenaHandlerObj: Property.object(),
        videoAdObj: Property.object(),
        cursorLeftHand: Property.object(),
        cursorRightHand: Property.object(),
        ambienceAudioObj: Property.object(),
    };

    start() {
        if (this.arenaHandlerObj) {
            this.networkHandler = this.arenaHandlerObj.getComponent('arena-handler');
        }
        
        // GC Optimization: Pre-allocate vectors and arrays to avoid runtime allocations
        this._initialPosition = vec3.create();
        this._hidePosition = vec3.fromValues(0, -1000, 0); // Far below the world to hide the lobby UI
        this._fadeColor = [0, 0, 0];
        
        this.object.getPositionWorld(this._initialPosition);
        
        // JS-to-WASM Optimization: Cache components upfront
        if (this.videoAdObj) {
            this.videoAdComponent = this.videoAdObj.getComponent(HeyVRAd);
            this.videoAdCursorTarget = this.videoAdObj.getComponent(CursorTarget);
            this.videoAdColliderComponent = this.videoAdObj.getComponent(CollisionComponent);
        }
        if (this.cursorLeftHand) {
            this.leftCursorComponent = this.cursorLeftHand.getComponent(Cursor);
        }
        if (this.cursorRightHand) {
            this.rightCursorComponent = this.cursorRightHand.getComponent(Cursor);
        }
        
        // Find the SoundAmbience child of arenaHandlerObj and cache its audio source
        if (this.ambienceAudioObj) {
            this._ambienceAudio = this.ambienceAudioObj.getComponent(HowlerAudioSource);
        }
        
        // GC Optimization: Pre-bind the event handler to avoid closure allocations
        this._onKeyDown = this._onKeyDown.bind(this);
        window.addEventListener('keydown', this._onKeyDown);
    }

    _onKeyDown(e) {
        if (e.key === 'Enter') {
            this.startGame();
        }
    }

    // Helper: Safely toggle component without expensive try/catch blocks
    _setComponentActive(comp, isActive) {
        if (comp && !comp.isDestroyed) {
            comp.active = isActive;
        }
    }

    async startGame() {
        // Capture session generation to detect if session was reset during the fade
        const gen = this.networkHandler ? this.networkHandler._sessionGen : undefined;
        // Start fade to black immediately when play button is hit
        if (this.networkHandler && typeof this.networkHandler._startFade === 'function') {
            this.networkHandler._startFade(1, this._fadeColor, 0.5, () => {
                // Discard if session was reset during the fade
                if (gen !== undefined && this.networkHandler._sessionGen !== gen) return;
                this._completeStartGameSetup();
            });
        } else {
            // Fallback if fade system not available
            await this._completeStartGameSetup();
        }
    }

    async _completeStartGameSetup() {
        // After fade completes, teleport and setup
        this.object.setPositionWorld(this._hidePosition);
        
        // CPU Optimization: Removed try/catch around active toggles
        this._setComponentActive(this.videoAdComponent, false);
        this._setComponentActive(this.videoAdColliderComponent, false);
        this._setComponentActive(this.videoAdCursorTarget, false);
        
        this._setComponentActive(this.leftCursorComponent, false);
        this._setComponentActive(this.rightCursorComponent, false);
        
        // Start ambient music loop for game session
        if (this._ambienceAudio) this._ambienceAudio.play();
        
        if (this.networkHandler && !this.networkHandler._setupFlag) {
            await this.networkHandler.setup();
        }
    }

    _resetSession() {
        // Stop ambient music loop when session ends
        if (this._ambienceAudio) this._ambienceAudio.stop();

        // Reset lobby state: move lobby UI back to initial position
        if (this.object && !this.object.isDestroyed && this._initialPosition) {
            this.object.setPositionWorld(this._initialPosition);
        }

        // Re-enable PlayButton so it can be shot again
        if (this.btnObj && !this.btnObj.isDestroyed) {
            this.btnObj.active = true;
        }

        // CPU Optimization: Re-enable interaction components without try/catch
        this._setComponentActive(this.videoAdComponent, true);
        this._setComponentActive(this.videoAdColliderComponent, true);
        this._setComponentActive(this.videoAdCursorTarget, true);

        // Re-enable lobby cursors
        this._setComponentActive(this.leftCursorComponent, true);
        this._setComponentActive(this.rightCursorComponent, true);
    }

    onDestroy() {
        window.removeEventListener('keydown', this._onKeyDown);
    }
}
